import { useQuery } from "@tanstack/react-query";
import { useParams, useLocation, Link } from "wouter";
import { Helmet } from "react-helmet-async";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ChevronRight, AlertTriangle, Calendar } from "lucide-react";
import { format } from "date-fns";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import type { Page, LegalDocVersion } from "@shared/schema";

interface VersionResponse {
  page: Page;
  version: LegalDocVersion;
}

export default function LegalVersionPage() {
  const params = useParams<{ slug: string; version: string }>();
  const [, setLocation] = useLocation();

  const { data, isLoading, error } = useQuery<VersionResponse>({
    queryKey: ["/api/legal/version", params.slug, params.version],
    queryFn: async () => {
      const res = await fetch(`/api/legal/${params.slug}/versions/${params.version}`);
      if (!res.ok) throw new Error("Version not found");
      return res.json();
    },
    enabled: !!params.slug && !!params.version,
    staleTime: Infinity,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <SiteHeader />
        <main className="flex-1">
          <div className="container mx-auto px-4 py-12 max-w-4xl">
            <Skeleton className="h-10 w-64 mb-4" />
            <Skeleton className="h-6 w-48 mb-8" />
            <div className="space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-4 w-full" />
              ))}
            </div>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <SiteHeader />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">Version Not Found</h1>
            <p className="text-muted-foreground mb-6">
              This archived version doesn't exist or has been removed.
            </p>
            <Button onClick={() => params.slug && setLocation(`/page/${params.slug}`)} data-testid="button-go-current">
              <ArrowLeft className="mr-2 h-4 w-4" />
              View current version
            </Button>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const { page, version } = data;

  return (
    <>
      <Helmet>
        <title>{page.title} — Version {version.versionNumber} | Future Manufacturing</title>
      </Helmet>

      <div className="min-h-screen flex flex-col bg-background">
        <SiteHeader />

        <main className="flex-1">
          <div className="container mx-auto px-4 py-10 max-w-4xl">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-1.5 text-sm text-muted-foreground mb-6" aria-label="Breadcrumb">
              <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
              <ChevronRight className="h-3.5 w-3.5 shrink-0" />
              <Link href="/legal" className="hover:text-foreground transition-colors">Legal</Link>
              <ChevronRight className="h-3.5 w-3.5 shrink-0" />
              <Link href={`/page/${page.slug}`} className="hover:text-foreground transition-colors">{page.title}</Link>
              <ChevronRight className="h-3.5 w-3.5 shrink-0" />
              <span className="text-foreground font-medium">Version {version.versionNumber}</span>
            </nav>

            {/* Archived version banner */}
            <div className="mb-8 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 flex items-start gap-3" data-testid="banner-archived-version">
              <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold text-amber-800 dark:text-amber-200">Archived version — not current</p>
                <p className="text-amber-700 dark:text-amber-300 mt-0.5">
                  You are viewing Version {version.versionNumber} of this document, archived on {format(new Date(version.createdAt), "d MMMM yyyy")}.
                  {" "}<Link href={`/page/${page.slug}`} className="underline hover:no-underline font-medium">View the current version →</Link>
                </p>
              </div>
            </div>

            <header className="mb-8">
              <h1 className="text-3xl md:text-4xl font-bold mb-3" data-testid="text-page-title">
                {page.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                <span className="font-mono font-semibold text-foreground">Version {version.versionNumber}</span>
                {version.effectiveDate && (
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" />
                    Effective {format(new Date(version.effectiveDate), "d MMMM yyyy")}
                  </span>
                )}
                {version.changeDescription && (
                  <span className="italic">"{version.changeDescription}"</span>
                )}
              </div>
            </header>

            {version.content && (
              <article
                className="prose prose-lg dark:prose-invert max-w-none [&_p]:mb-4 [&_h1]:text-2xl [&_h2]:text-xl [&_h3]:text-lg [&_a]:text-primary [&_a]:underline [&_table]:w-full [&_th]:text-left [&_th]:p-2 [&_td]:p-2 [&_table]:border-collapse [&_th]:border [&_td]:border"
                dangerouslySetInnerHTML={{ __html: version.content }}
                data-testid="content-version-body"
              />
            )}

            <footer className="mt-12 pt-6 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setLocation(`/page/${page.slug}`)}
                data-testid="button-view-current"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                View current version
              </Button>
            </footer>
          </div>
        </main>

        <SiteFooter />
      </div>
    </>
  );
}
